/** Placeholder substituted for a value that cannot be safely serialized. */
export const UNSERIALIZABLE = '[unserializable]';

/** Guards against pathological nesting; log payloads are never legitimately deep. */
const MAX_DEPTH = 8;

/**
 * Read a diagnostic type name without touching the value itself.
 *
 * The name is taken from the *prototype*, which is an ordinary object, rather than
 * from the value — see {@link sanitizeForJson} for why reading a property off the
 * value is what we are avoiding.
 */
function describe(proto: object | null): string {
  try {
    const name = proto?.constructor?.name;
    return typeof name === 'string' && name.length > 0
      ? `[unserializable: ${name}]`
      : UNSERIALIZABLE;
  } catch {
    return UNSERIALIZABLE;
  }
}

/**
 * Replace anything `JSON.stringify` cannot safely handle, before it sees it.
 *
 * `JSON.stringify` probes `value.toJSON` and **calls it** before any replacer runs.
 * Every property on a Cloudflare Workers RPC stub is a callable proxy, so that probe
 * dispatches a real RPC call named `toJSON` which nobody awaits. The abandoned call
 * leaves a server-side invocation open until the runtime cancels it as hung — which
 * is what filled the logs with `toJSON` noise (LIFT-2529).
 *
 * Because the probe happens before the replacer, a `JSON.stringify` replacer cannot
 * prevent this. The only reliable defence is to never hand a stub to
 * `JSON.stringify` at all, which is what this does.
 *
 * The check is an allowlist: recognised plain data is kept and everything else
 * becomes a placeholder, so an unfamiliar object type is safe by default rather than
 * safe only if we anticipated it. Unknown objects are classified with
 * `Object.getPrototypeOf` and `instanceof`, which use internal methods rather than
 * named property gets — on an RPC stub, reading a named property is itself enough to
 * dispatch a call.
 */
export function sanitizeForJson(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (value === null) return null;

  switch (typeof value) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'undefined':
      return value;
    case 'bigint':
      return String(value);
    case 'function':
    case 'symbol':
      return UNSERIALIZABLE;
    default:
      break;
  }

  const object = value as object;

  // Types that carry meaning in logs and are safe to convert without reading
  // arbitrary properties off the value.
  if (object instanceof Error) {
    return {
      name: object.name,
      message: object.message,
      stack: object.stack,
      ...(object.cause !== undefined && {
        cause: sanitizeForJson(object.cause, depth + 1, seen),
      }),
    };
  }
  if (object instanceof Date) return object.toISOString();
  if (object instanceof URL) return object.href;

  if (depth >= MAX_DEPTH) return '[max depth exceeded]';
  if (seen.has(object)) return '[circular]';

  if (Array.isArray(object)) {
    seen.add(object);
    return object.map((item) => sanitizeForJson(item, depth + 1, seen));
  }

  const proto = Object.getPrototypeOf(object) as object | null;
  const isPlainObject = proto === Object.prototype || proto === null;
  if (!isPlainObject) return describe(proto);

  seen.add(object);
  const result: Record<string, unknown> = {};
  for (const [key, entryValue] of Object.entries(object)) {
    result[key] = sanitizeForJson(entryValue, depth + 1, seen);
  }
  return result;
}
